(() => {
  const tabletQuery = window.matchMedia('(min-width: 768px) and (max-width: 1199px)');
  const compactVehicleQuery = window.matchMedia('(max-width: 1199px)');
  const desktopQuery = window.matchMedia('(min-width: 1200px)');
  const orangeButtonPattern = /rgba?\(\s*255\s*,\s*(?:90|107|122)\s*,\s*(?:0|26)(?:\s*,\s*[^)]+)?\s*\)/i;
  const blueButtonPattern = /rgba?\(\s*0\s*,\s*(?:86|104)\s*,\s*(?:179|215)(?:\s*,\s*[^)]+)?\s*\)/i;
  const buttonSelector = 'button, a.button, [role="button"], input[type="submit"], input[type="button"], .btn-comprar, .tb-button, .ta-auto-card__cart, .ta-load-more-button, .tomauto-cart-empty__button, .checkout-button, .slider-arrow';
  const linkSelector = 'a[href], [role="link"], .read-more';
  const headerScopeSelector = '.header-wrapper, header, #mobile-menu-drawer, #custom-menu-drawer, .tm-clean-nav, .tm-clean-search';
  const orangeQueue = new Set();
  let orangeNormalizationScheduled = false;
  let tabletHeaderMotionCleanup = null;
  let tabletDockCleanup = null;
  let tabletMenuScrollY = 0;

  /*
   * Performance: separar leituras de estilo e escritas no DOM.
   * O código anterior fazia getComputedStyle() -> escrita -> getComputedStyle()
   * repetidamente em dezenas de elementos, o que podia obrigar o browser a
   * recalcular estilos/layout entre elementos.
   */
  function preserveLinkRestColors(scope = document) {
    const links = scope.nodeType === Node.ELEMENT_NODE
      ? [scope, ...scope.querySelectorAll(linkSelector)]
      : [...scope.querySelectorAll(linkSelector)];

    const updates = [];

    links.forEach((link) => {
      if (!link.matches?.(linkSelector) || link.dataset.tomautoLinkRestColor) return;
      updates.push({
        link,
        color: window.getComputedStyle(link).color
      });
    });

    updates.forEach(({ link, color }) => {
      link.style.setProperty('--tomauto-link-rest-color', color);
      link.dataset.tomautoLinkRestColor = 'true';
    });
  }

  function neutralizeButtonShadows(scope = document) {
    const buttons = scope.nodeType === Node.ELEMENT_NODE
      ? [scope, ...scope.querySelectorAll(buttonSelector)]
      : [...scope.querySelectorAll(buttonSelector)];

    const updates = [];

    buttons.forEach((button) => {
      if (!button.matches?.(buttonSelector)) return;

      if (button.matches('.tab-button')) {
        updates.push({ button, tabButton: true, neutral: null });
        return;
      }

      const current = window.getComputedStyle(button).boxShadow;
      if (!current || current === 'none') return;

      updates.push({
        button,
        tabButton: false,
        neutral: current.replace(/rgba?\([^)]+\)/gi, 'rgb(209, 218, 223)')
      });
    });

    updates.forEach(({ button, tabButton, neutral }) => {
      if (tabButton) {
        button.style.removeProperty('box-shadow');
        delete button.dataset.tomautoNeutralShadow;
        return;
      }

      button.style.setProperty('box-shadow', neutral, 'important');
      button.dataset.tomautoNeutralShadow = 'true';
    });
  }

  function darkenOrangeButtons(scope = document) {
    const elements = scope.nodeType === Node.ELEMENT_NODE
      ? [scope, ...scope.querySelectorAll(buttonSelector)]
      : [...scope.querySelectorAll(buttonSelector)];

    const borderProperties = ['border-top-color', 'border-right-color', 'border-bottom-color', 'border-left-color'];
    const updates = [];

    elements.forEach((element) => {
      if (!element.matches?.(buttonSelector)) return;
      if (element.closest(headerScopeSelector)) return;

      const computed = window.getComputedStyle(element);
      const orangeBackground =
        orangeButtonPattern.test(computed.backgroundColor) ||
        orangeButtonPattern.test(computed.backgroundImage);
      const orangeForeground = orangeButtonPattern.test(computed.color);
      const orangeBorders = borderProperties.filter((property) =>
        orangeButtonPattern.test(computed.getPropertyValue(property))
      );

      if (!orangeBackground && !orangeForeground && orangeBorders.length === 0) return;

      updates.push({
        element,
        orangeBackground,
        orangeForeground,
        orangeBorders
      });
    });

    updates.forEach(({ element, orangeBackground, orangeForeground, orangeBorders }) => {
      if (orangeBackground) {
        element.dataset.tomautoDarkButton = 'true';
        element.style.setProperty('background-color', '#222F35', 'important');
        element.style.setProperty('background-image', 'none', 'important');
        element.style.setProperty('color', '#FFFFFF', 'important');
        element.style.setProperty('--color-button', '34, 47, 53', 'important');
        element.style.setProperty('--color-button-text', '255, 255, 255', 'important');
      } else if (orangeForeground) {
        element.style.setProperty('color', '#222F35', 'important');
      }

      orangeBorders.forEach((property) => {
        element.style.setProperty(property, '#222F35', 'important');
      });
    });
  }

  function darkenBlueButtons(scope = document) {
    const elements = scope.nodeType === Node.ELEMENT_NODE
      ? [scope, ...scope.querySelectorAll(buttonSelector)]
      : [...scope.querySelectorAll(buttonSelector)];

    const borderProperties = ['border-top-color', 'border-right-color', 'border-bottom-color', 'border-left-color'];
    const updates = [];

    elements.forEach((element) => {
      if (!element.matches?.(buttonSelector)) return;

      const computed = window.getComputedStyle(element);
      const blueBackground =
        blueButtonPattern.test(computed.backgroundColor) ||
        blueButtonPattern.test(computed.backgroundImage);
      const blueForeground = blueButtonPattern.test(computed.color);
      const blueBorders = borderProperties.filter((property) =>
        blueButtonPattern.test(computed.getPropertyValue(property))
      );

      if (!blueBackground && !blueForeground && blueBorders.length === 0) return;

      updates.push({
        element,
        blueBackground,
        blueForeground,
        blueBorders
      });
    });

    updates.forEach(({ element, blueBackground, blueForeground, blueBorders }) => {
      if (blueBackground) {
        element.dataset.tomautoBlueButton = 'true';
        element.style.setProperty('background-color', '#2F3E44', 'important');
        element.style.setProperty('background-image', 'none', 'important');
        element.style.setProperty('color', '#FFFFFF', 'important');
        element.style.setProperty('--color-button', '47, 62, 68', 'important');
        element.style.setProperty('--color-button-text', '255, 255, 255', 'important');
      } else if (blueForeground) {
        element.style.setProperty('color', '#2F3E44', 'important');
      }

      blueBorders.forEach((property) => {
        element.style.setProperty(property, '#2F3E44', 'important');
      });
    });
  }

  function scheduleOrangeNormalization(scope = document) {
    orangeQueue.add(scope);
    if (orangeNormalizationScheduled) return;

    orangeNormalizationScheduled = true;
    const run = () => {
      const scopes = [...orangeQueue];
      orangeQueue.clear();
      orangeNormalizationScheduled = false;

      if (scopes.includes(document)) {
        preserveLinkRestColors(document);
        darkenOrangeButtons(document);
        darkenBlueButtons(document);
        neutralizeButtonShadows(document);
        return;
      }

      scopes.forEach((queuedScope) => {
        preserveLinkRestColors(queuedScope);
        darkenOrangeButtons(queuedScope);
        darkenBlueButtons(queuedScope);
        neutralizeButtonShadows(queuedScope);
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 700 });
    } else {
      window.setTimeout(run, 0);
    }
  }

  function enhanceTabletHeader() {
    tabletHeaderMotionCleanup?.();
    tabletHeaderMotionCleanup = null;
    if (!tabletQuery.matches) return;

    const header = document.querySelector('.header-wrapper');
    const compact = document.querySelector('.tm-mobile-compact-header');
    if (!header || !compact) return;

    const root = document.documentElement;
    let lastY = Math.max(window.scrollY, 0);
    let accumulatedDelta = 0;
    let ticking = false;
    let currentMode = null;

    const overlayOpen = () =>
      document.body.classList.contains('has-menu-open') ||
      document.body.classList.contains('tomauto-mobile-menu-open') ||
      document.body.classList.contains('tm-clean-search-open') ||
      document.documentElement.classList.contains('tm-clean-search-open');

    const showFull = () => {
      currentMode = 'full';
      root.classList.remove('tm-tablet-compact-active');
      header.classList.remove('tm-full-header--hidden');
      header.classList.add('tm-full-header--visible');
      compact.classList.remove('is-visible');
      compact.setAttribute('aria-hidden', 'true');
    };

    const showCompact = () => {
      currentMode = 'compact';
      root.classList.add('tm-tablet-compact-active');
      header.classList.remove('tm-full-header--visible');
      header.classList.add('tm-full-header--hidden');
      compact.classList.add('is-visible');
      compact.setAttribute('aria-hidden', 'false');
    };

    const update = () => {
      const currentY = Math.max(window.scrollY, 0);

      if (currentY <= 40 || overlayOpen()) {
        showFull();
        accumulatedDelta = 0;
      } else {
        const delta = currentY - lastY;
        if (!Number.isFinite(delta) || Math.abs(delta) < 2) {
          lastY = currentY;
          ticking = false;
          return;
        }

        if ((delta > 0 && accumulatedDelta < 0) || (delta < 0 && accumulatedDelta > 0)) {
          accumulatedDelta = 0;
        }

        accumulatedDelta += delta;

        if (currentMode === 'full' && accumulatedDelta >= 20) {
          showCompact();
          accumulatedDelta = 0;
        } else if (currentMode === 'compact' && accumulatedDelta <= -24) {
          showFull();
          accumulatedDelta = 0;
        }
      }

      lastY = currentY;
      ticking = false;
    };

    const onMovement = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    const onPageShow = () => {
      lastY = Math.max(window.scrollY, 0);
      accumulatedDelta = 0;
      showFull();
    };

    showFull();
    window.addEventListener('scroll', onMovement, { passive: true });
    window.addEventListener('resize', onMovement);
    window.addEventListener('pageshow', onPageShow);

    tabletHeaderMotionCleanup = () => {
      window.removeEventListener('scroll', onMovement);
      window.removeEventListener('resize', onMovement);
      window.removeEventListener('pageshow', onPageShow);
      root.classList.remove('tm-tablet-compact-active');
    };
  }

    function enhanceTabletDock() {
    tabletDockCleanup?.();
    tabletDockCleanup = null;
    if (!tabletQuery.matches) return;

    const nav = document.querySelector('[data-tm-clean-nav]');
    const searchPanel = document.querySelector('[data-tm-clean-search]');
    const drawer = document.getElementById('mobile-menu-drawer');
    const overlay = document.getElementById('mobile-menu-overlay');
    if (!nav) return;

    let lastY = Math.max(window.scrollY, 0);
    let accumulatedMovement = 0;
    let movementDirection = 0;

    const showNav = () => {
      nav.classList.remove('is-hidden');
      nav.setAttribute('aria-hidden', 'false');
    };

    const hideNav = () => {
      nav.classList.add('is-hidden');
      nav.setAttribute('aria-hidden', 'true');
    };

    const setActive = (key) => {
      nav.querySelectorAll('[data-nav-key]').forEach((item) => {
        const active = item.dataset.navKey === key;
        item.classList.toggle('is-active', active);
        if (active) item.setAttribute('aria-current', 'page');
        else item.removeAttribute('aria-current');
      });
    };

    const updateActive = () => {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      if (path === '/') setActive('home');
      else if (path.startsWith('/cart')) setActive('cart');
      else if (path.startsWith('/account')) setActive('account');
      else setActive('');
    };

    const openSearch = () => {
      if (!searchPanel) return;
      setActive('search');
      searchPanel.classList.add('is-open');
      searchPanel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('tm-clean-search-open');
      document.body.classList.add('tm-clean-search-open');
      window.setTimeout(() => searchPanel.querySelector('[data-tm-search-input]')?.focus(), 80);
    };

    const closeSearch = () => {
      if (!searchPanel) return;
      searchPanel.classList.remove('is-open');
      searchPanel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('tm-clean-search-open');
      document.body.classList.remove('tm-clean-search-open');
      updateActive();
      showNav();
    };

    const openMenuFallback = () => {
      if (!drawer || !overlay || drawer.classList.contains('open')) return;
      tabletMenuScrollY = Math.max(window.scrollY, 0);
      document.documentElement.classList.add('tomauto-mobile-menu-open');
      document.body.classList.add('tomauto-mobile-menu-open', 'has-menu-open');
      document.body.style.top = `-${tabletMenuScrollY}px`;
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
      overlay.classList.add('active');
      overlay.setAttribute('aria-hidden', 'false');
      document.getElementById('mobile-drawer-close')?.classList.add('visible');
      document.getElementById('mobile-menu-button')?.setAttribute('aria-expanded', 'true');
    };

    const closeMenuFallback = () => {
      if (!drawer?.classList.contains('open')) return;
      drawer.classList.remove('open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay?.classList.remove('active');
      overlay?.setAttribute('aria-hidden', 'true');
      document.getElementById('mobile-drawer-close')?.classList.remove('visible');
      document.getElementById('mobile-menu-button')?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('tomauto-mobile-menu-open');
      document.body.classList.remove('tomauto-mobile-menu-open', 'has-menu-open');
      ['top', 'position', 'left', 'right', 'width', 'overflow'].forEach((property) => {
        document.body.style.removeProperty(property);
      });
      window.scrollTo(0, tabletMenuScrollY);
      updateActive();
      showNav();
    };

    const onClick = (event) => {
      const menuButton = event.target.closest?.('[data-tm-open-header-menu]');
      if (menuButton) {
        event.preventDefault();
        setActive('menu');
        showNav();
        const headerButton = document.getElementById('mobile-menu-button');
        headerButton?.click();
        window.requestAnimationFrame(openMenuFallback);
        return;
      }

      if (event.target.closest?.('[data-tm-open-search]')) {
        event.preventDefault();
        openSearch();
        return;
      }

      if (event.target.closest?.('[data-tm-close-search]')) {
        event.preventDefault();
        closeSearch();
        return;
      }

      if (event.target === overlay || event.target.closest?.('#mobile-drawer-close')) {
        window.requestAnimationFrame(closeMenuFallback);
      }
    };

    const onScroll = () => {
      if (drawer?.classList.contains('open') || searchPanel?.classList.contains('is-open')) return;
      const currentY = Math.max(window.scrollY, 0);
      const delta = currentY - lastY;
      lastY = currentY;
      if (!Number.isFinite(delta) || delta === 0) return;

      const direction = delta > 0 ? 1 : -1;
      if (direction !== movementDirection) {
        movementDirection = direction;
        accumulatedMovement = 0;
      }
      accumulatedMovement += Math.abs(delta);
      if (accumulatedMovement < 8) return;
      if (direction > 0) hideNav();
      else showNav();
      accumulatedMovement = 0;
    };

    const drawerObserver = drawer
      ? new MutationObserver(() => {
          if (drawer.classList.contains('open')) hideNav();
          else {
            updateActive();
            showNav();
          }
        })
      : null;

    drawerObserver?.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    nav.dataset.tomautoTabletDockReady = 'true';
    nav.addEventListener('click', onClick, true);
    document.addEventListener('click', onClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    updateActive();
    showNav();

    tabletDockCleanup = () => {
      drawerObserver?.disconnect();
      delete nav.dataset.tomautoTabletDockReady;
      nav.removeEventListener('click', onClick, true);
      document.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScroll);
    };
  }

  function translateReviewLinks(scope = document) {
    const elements = scope.nodeType === Node.ELEMENT_NODE
      ? [scope, ...scope.querySelectorAll('a, button')]
      : [...scope.querySelectorAll('a, button')];

    elements.forEach((element) => {
      if (element.textContent.trim().toLowerCase() === 'write a review') {
        element.textContent = 'Escrever uma avaliação';
      }
    });
  }

  function createScrollControl(className, label, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `tm-scroll-control ${className}`;
    button.setAttribute('aria-label', label);
    let handledByPointer = false;
    const stopEvent = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };
    const activate = () => {
      try {
        onClick();
        button.dataset.tomautoActivated = 'true';
        delete button.dataset.tomautoError;
      } catch (error) {
        button.dataset.tomautoError = String(error);
      }
    };
    button.addEventListener('pointerdown', (event) => {
      stopEvent(event);
      handledByPointer = true;
      activate();
    }, true);
    button.addEventListener('click', (event) => {
      stopEvent(event);
      if (!handledByPointer || event.detail === 0) activate();
      handledByPointer = false;
    }, true);
    document.body.appendChild(button);
    return button;
  }

  let pageScrollUp;
  let pageScrollDown;
  let selectScrollUp;
  let selectScrollDown;
  let observedSelectPortal;

  function syncPageScrollControls() {
    if (!pageScrollUp || !pageScrollDown) return;
    const scrollRoot = document.scrollingElement || document.documentElement;
    const available = desktopQuery.matches && scrollRoot.scrollHeight > window.innerHeight + 2;
    pageScrollUp.classList.toggle('is-visible', available);
    pageScrollDown.classList.toggle('is-visible', available);
    pageScrollUp.disabled = !available || window.scrollY <= 2;
    pageScrollDown.disabled = !available || window.scrollY + window.innerHeight >= scrollRoot.scrollHeight - 2;
  }

  function syncSelectScrollControls() {
    /* Nada deste controlo é necessário no mobile/tablet. */
    if (!desktopQuery.matches || !selectScrollUp || !selectScrollDown) return;
    const portal = document.getElementById('tm-desktop-select-portal');
    const available = portal?.classList.contains('is-open') && portal.scrollHeight > portal.clientHeight + 2;

    selectScrollUp.classList.toggle('is-visible', Boolean(available));
    selectScrollDown.classList.toggle('is-visible', Boolean(available));
    if (!available) return;

    const rect = portal.getBoundingClientRect();
    const right = Math.max(0, window.innerWidth - rect.right);
    selectScrollUp.style.top = `${Math.max(0, rect.top)}px`;
    selectScrollUp.style.right = `${right}px`;
    selectScrollDown.style.top = `${Math.max(0, rect.bottom - 18)}px`;
    selectScrollDown.style.right = `${right}px`;
    selectScrollUp.disabled = portal.scrollTop <= 1;
    selectScrollDown.disabled = portal.scrollTop + portal.clientHeight >= portal.scrollHeight - 1;

    if (portal !== observedSelectPortal) {
      observedSelectPortal = portal;
      new MutationObserver(syncSelectScrollControls).observe(portal, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true
      });
      portal.addEventListener('scroll', syncSelectScrollControls, { passive: true });
    }
  }

  function enhanceDesktopScrollControls() {
    document.querySelectorAll('.tm-page-scroll-control').forEach((control) => control.remove());
    pageScrollUp = null;
    pageScrollDown = null;

    /* Não criar controlos de scroll desktop em mobile/tablet. */
    if (!desktopQuery.matches) {
      selectScrollUp?.remove();
      selectScrollDown?.remove();
      selectScrollUp = null;
      selectScrollDown = null;
      observedSelectPortal = null;
      return;
    }

    if (!selectScrollUp) {
      selectScrollUp = createScrollControl(
        'tm-select-scroll-control tm-select-scroll-control--up tm-scroll-control--up',
        'Subir nas opções',
        () => {
          const portal = document.getElementById('tm-desktop-select-portal');
          if (!portal) return;
          const target = Math.max(0, portal.scrollTop - Math.max(120, portal.clientHeight * .7));
          portal.scrollTo(0, target);
          selectScrollUp.dataset.tomautoLastTarget = String(target);
          window.setTimeout(syncSelectScrollControls, 0);
        }
      );
      selectScrollDown = createScrollControl(
        'tm-select-scroll-control tm-select-scroll-control--down tm-scroll-control--down',
        'Descer nas opções',
        () => {
          const portal = document.getElementById('tm-desktop-select-portal');
          if (!portal) return;
          const target = Math.min(
            portal.scrollHeight - portal.clientHeight,
            portal.scrollTop + Math.max(120, portal.clientHeight * .7)
          );
          portal.scrollTo(0, target);
          selectScrollDown.dataset.tomautoLastTarget = String(target);
          window.setTimeout(syncSelectScrollControls, 0);
        }
      );
    }

    window.setTimeout(syncSelectScrollControls, 0);
  }

  function openCatalogHelpWithoutMenu(event) {
    const opener = event.target.closest?.('#open-catalog-help');
    if (!opener) return;

    const modal = document.getElementById('catalog-help-modal');
    if (!modal) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    modal.style.display = 'flex';
    window.requestAnimationFrame(() => modal.classList.add('show'));
    document.body.style.overflow = 'hidden';
  }

  function enhanceProduct(scope = document) {
    const roots = scope.matches?.("product-info[id^='MainProduct-']")
      ? [scope]
      : [...scope.querySelectorAll("product-info[id^='MainProduct-']")];

    roots.forEach((root) => {
      const purchase = root.querySelector('.listing-item__right');
      const sourceTitle = root.querySelector('.product-main-title');
      const priceInfo = purchase?.querySelector('.listing-item__price-info');

      if (purchase && sourceTitle && priceInfo && !purchase.querySelector('.tomauto-product-title-tablet')) {
        const title = document.createElement('div');
        title.className = 'tomauto-product-title-tablet';
        title.textContent = sourceTitle.textContent.trim();
        purchase.insertBefore(title, priceInfo);
      }

      root.querySelectorAll('.tomauto-reference-link[data-open-oem]').forEach((button) => {
        if (!button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', 'false');
      });
    });
  }
    function toggleTabletReferences(event) {
    const button = event.target.closest?.('.tomauto-reference-link[data-open-oem]');
    if (!button || !tabletQuery.matches) return;

    const card = button.closest('.tomauto-reference-card');
    if (!card) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const isOpen = card.classList.toggle('is-open');
    button.setAttribute('aria-expanded', String(isOpen));
    button.setAttribute('aria-label', isOpen ? 'Ocultar referências' : 'Ver referências');
    button.setAttribute('title', isOpen ? 'Ocultar referências' : 'Ver referências');
    button.textContent = isOpen ? '−' : '+';
  }

  function normalizeVehicleSelectorTriggers(scope = document) {
    const wrappers = scope.matches?.('.hero-search-pro .select-wrapper')
      ? [scope]
      : [...scope.querySelectorAll?.('.hero-search-pro .select-wrapper') || []];

    wrappers.forEach((wrapper) => {
      if (compactVehicleQuery.matches) {
        wrapper.querySelectorAll(':scope > .tm-desktop-select-trigger').forEach((trigger) => trigger.remove());
        wrapper.classList.remove('tm-desktop-select-ready');
      } else {
        wrapper.querySelectorAll(':scope > .tomauto-mobile-select-trigger').forEach((trigger) => trigger.remove());
      }

      wrapper.querySelectorAll(':scope > .vehicle-select').forEach((select) => {
        if (compactVehicleQuery.matches) {
          select.style.setProperty('display', 'none', 'important');
          select.dataset.tomautoCompactHidden = 'true';
        } else if (select.dataset.tomautoCompactHidden) {
          select.style.removeProperty('display');
          delete select.dataset.tomautoCompactHidden;
        }
      });

      const triggers = [...wrapper.querySelectorAll(':scope > .tomauto-mobile-select-trigger')];
      triggers.slice(1).forEach((duplicate) => duplicate.remove());
    });

    if (compactVehicleQuery.matches) {
      const portal = document.getElementById('tm-desktop-select-portal');
      portal?.classList.remove('is-open');
      portal?.setAttribute('aria-hidden', 'true');
      if (portal) portal.innerHTML = '';
    }
  }

  function syncFacetsLayer() {
    const isOpen = Boolean(document.querySelector(
      '.mobile-facets__disclosure[open], .mobile-facets__disclosure.menu-opening, .mobile-facets__wrapper details[open].menu-opening, .tomauto-search-sort-modal.is-open, .tomauto-search-sort-modal[aria-hidden="false"]'
    ));
    document.body.classList.toggle('tomauto-facets-open', isOpen);
  }

  function runEnhancements(scope = document) {
    normalizeVehicleSelectorTriggers(scope);
    syncFacetsLayer();
    enhanceProduct(scope);
    enhanceTabletHeader();
    enhanceTabletDock();
    enhanceDesktopScrollControls();
    translateReviewLinks(scope);
    scheduleOrangeNormalization(scope);
  }

  let enhancementsStarted = false;

  const startEnhancements = () => {
    if (enhancementsStarted) return;
    enhancementsStarted = true;
    runEnhancements();
  };

  if (document.readyState === 'complete') {
    startEnhancements();
  } else {
    document.addEventListener('DOMContentLoaded', startEnhancements, { once: true });

    /* Se o ficheiro for carregado dinamicamente depois do evento, inicia no próximo ciclo. */
    if (document.readyState === 'interactive') {
      window.setTimeout(() => {
        if (!tabletQuery.matches || document.querySelector('.tm-mobile-compact-header')) {
          startEnhancements();
        }
      }, 0);
    }
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        enhanceProduct(node);
        normalizeVehicleSelectorTriggers(node);
        translateReviewLinks(node);
        window.setTimeout(syncSelectScrollControls, 0);
        scheduleOrangeNormalization(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  window.addEventListener('load', () => {
    enhanceProduct(document);
    translateReviewLinks(document);
    enhanceDesktopScrollControls();
    scheduleOrangeNormalization(document);
  });

  tabletQuery.addEventListener?.('change', () => runEnhancements());
  compactVehicleQuery.addEventListener?.('change', () => runEnhancements());
  desktopQuery.addEventListener?.('change', enhanceDesktopScrollControls);

  window.addEventListener('resize', enhanceDesktopScrollControls, {
    passive: true
  });

  window.addEventListener('scroll', syncPageScrollControls, {
    passive: true
  });

  document.addEventListener(
    'shopify:section:load',
    (event) => runEnhancements(event.target)
  );

  document.addEventListener(
    'click',
    openCatalogHelpWithoutMenu,
    true
  );

  document.addEventListener(
    'click',
    () => window.setTimeout(syncSelectScrollControls, 0)
  );

  document.addEventListener(
    'click',
    () => window.setTimeout(syncFacetsLayer, 0),
    true
  );

  document.addEventListener(
    'toggle',
    syncFacetsLayer,
    true
  );

  document.addEventListener(
    'pointerover',
    (event) => {
      const button = event.target.closest?.(buttonSelector);
      if (button) scheduleOrangeNormalization(button);
    },
    true
  );

  document.addEventListener(
    'focusin',
    (event) => {
      const button = event.target.closest?.(buttonSelector);
      if (button) scheduleOrangeNormalization(button);
    },
    true
  );

  document.addEventListener(
    'click',
    toggleTabletReferences,
    true
  );
})();